const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface Condition {
  field: string;
  operator: string;
  value: any;
}

interface Rules {
  operator: 'AND' | 'OR';
  conditions: Condition[];
}

interface CreateSegment {
  name: string;
  description: string;
  inclusion_rules: Rules;
  exclusion_rules: Rules;
  priority: number;
  is_active: boolean;
  email_list_ready: boolean;
  never_contact: boolean;
  auto_tag: boolean;
}

interface Segment extends CreateSegment {
  id: number;
  active_leads: number;
  total_leads: number;
  conversion_rate?: number;
  last_evaluated?: string;
  created_at: string;
}

interface TestResult {
  total_leads: number;
  matching_count: number;
  excluded_count: number;
  matching_leads: any[];
  excluded_leads: any[];
  match_percentage: string;
}


class SegmentService {
  private getAuthHeaders() {
    const token = localStorage.getItem('chatbot_admin_token');
    console.log('🔑 SegmentService auth check:', { 
      tokenExists: !!token, 
      tokenLength: token?.length,
      tokenPreview: token?.substring(0, 20) + '...' 
    });
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  // Get all segments
  async getSegments(): Promise<Segment[]> {
    const response = await fetch(`${API_BASE_URL}/api/segments`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse<Segment[]>(response);
  }

  // Create new segment
  async createSegment(segment: CreateSegment): Promise<{ id: number; success: boolean }> {
    console.log('🚀 Creating segment:', { 
      url: `${API_BASE_URL}/api/segments`,
      method: 'POST',
      segment,
      headers: this.getAuthHeaders()
    });
    
    const response = await fetch(`${API_BASE_URL}/api/segments`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(segment)
    });
    
    console.log('📡 Segment creation response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      url: response.url
    });
    
    return this.handleResponse(response);
  }

  // Update segment
  async updateSegment(id: number, updates: Partial<Segment>): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/segments/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updates)
    });
    return this.handleResponse(response);
  }

  // Delete segment
  async deleteSegment(id: number): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/segments/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Test segment rules
  async testSegmentRules(inclusion_rules: Rules, exclusion_rules: Rules): Promise<TestResult> {
    const response = await fetch(`${API_BASE_URL}/api/segments/evaluate-rules`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ inclusion_rules, exclusion_rules })
    });
    return this.handleResponse<TestResult>(response);
  }

  // Get segment leads with pagination
  async getSegmentLeads(segmentId: number, page: number = 1, limit: number = 50) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    const response = await fetch(`${API_BASE_URL}/api/segments/${segmentId}/leads?${params}`, {
      headers: this.getAuthHeaders()
    });
    return this.handleResponse(response);
  }

  // Assign leads to segment
  async assignLeadsToSegment(segmentId: number, leadIds: number[]): Promise<{
    success: boolean;
    assigned: number;
    skipped: number;
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/segments/${segmentId}/assign-leads`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ lead_ids: leadIds })
    });
    return this.handleResponse(response);
  }

  // Remove leads from segment
  async removeLeadsFromSegment(segmentId: number, leadIds: number[]): Promise<{
    success: boolean;
    removed: number;
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/segments/${segmentId}/remove-leads`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ lead_ids: leadIds })
    });
    return this.handleResponse(response);
  }

  // Export segment as CSV
  async exportSegment(segmentId: number, format: 'csv' | 'json' = 'csv'): Promise<Blob | any> {
    const response = await fetch(`${API_BASE_URL}/api/segments/${segmentId}/export?format=${format}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('chatbot_admin_token')}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Export failed' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    if (format === 'csv') {
      return response.blob();
    } else {
      return response.json();
    }
  }

  // Clone segment
  async cloneSegment(segmentId: number, newName?: string): Promise<{
    success: boolean;
    id: number;
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/segments/${segmentId}/clone`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ name: newName })
    });
    return this.handleResponse(response);
  }

  // Toggle segment active status
  async toggleSegmentStatus(segmentId: number, isActive: boolean): Promise<{ success: boolean }> {
    return this.updateSegment(segmentId, { is_active: !isActive });
  }

  // Batch operations
  async batchUpdateSegments(updates: Array<{ id: number; updates: Partial<Segment> }>): Promise<{
    success: boolean;
    updated: number;
    failed: number;
  }> {
    const results = await Promise.allSettled(
      updates.map(({ id, updates }) => this.updateSegment(id, updates))
    );

    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    return {
      success: failed === 0,
      updated: successful,
      failed
    };
  }

  // Get segment analytics
  async getSegmentAnalytics(segmentId: number, timeRange: '7d' | '30d' | '90d' = '30d') {
    // This would be implemented when analytics are added
    // For now, return mock data structure
    return {
      lead_growth: [],
      conversion_rates: {},
      engagement_metrics: {},
      time_range: timeRange
    };
  }

  // Validate segment rules before saving
  validateSegmentRules(segment: Partial<Segment>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!segment.name?.trim()) {
      errors.push('Segment name is required');
    }

    if (!segment.inclusion_rules?.conditions?.length && !segment.never_contact) {
      errors.push('At least one inclusion rule is required');
    }

    if (segment.priority !== undefined && (segment.priority < 1 || segment.priority > 100)) {
      errors.push('Priority must be between 1 and 100');
    }

    // Validate rule structure
    if (segment.inclusion_rules) {
      const ruleErrors = this.validateRules(segment.inclusion_rules, 'inclusion');
      errors.push(...ruleErrors);
    }

    if (segment.exclusion_rules) {
      const ruleErrors = this.validateRules(segment.exclusion_rules, 'exclusion');
      errors.push(...ruleErrors);
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  private validateRules(rules: Rules, type: 'inclusion' | 'exclusion'): string[] {
    const errors: string[] = [];

    if (!['AND', 'OR'].includes(rules.operator)) {
      errors.push(`${type} rules must have AND or OR operator`);
    }

    rules.conditions.forEach((condition, index) => {
      if (!condition.field) {
        errors.push(`${type} rule ${index + 1}: Field is required`);
      }
      if (!condition.operator) {
        errors.push(`${type} rule ${index + 1}: Operator is required`);
      }
      if (condition.value === undefined || condition.value === null) {
        const needsValue = !['is_empty', 'is_not_empty'].includes(condition.operator);
        if (needsValue) {
          errors.push(`${type} rule ${index + 1}: Value is required`);
        }
      }
    });

    return errors;
  }

  // Utility methods
  formatSegmentForDisplay(segment: Segment) {
    return {
      ...segment,
      status: this.getSegmentStatus(segment),
      rulesSummary: this.generateRulesSummary(segment)
    };
  }

  private getSegmentStatus(segment: Segment): string {
    if (segment.never_contact) return 'Do Not Contact';
    if (!segment.is_active) return 'Inactive';
    if (segment.email_list_ready) return 'Email Ready';
    return 'Processing';
  }

  private generateRulesSummary(segment: Segment): string {
    const inclusionCount = segment.inclusion_rules?.conditions?.length || 0;
    const exclusionCount = segment.exclusion_rules?.conditions?.length || 0;
    
    return `${inclusionCount} inclusion rule${inclusionCount !== 1 ? 's' : ''}${
      exclusionCount > 0 ? `, ${exclusionCount} exclusion rule${exclusionCount !== 1 ? 's' : ''}` : ''
    }`;
  }

  // Error handling utilities
  isNetworkError(error: Error): boolean {
    return error.message.includes('Network') || error.message.includes('fetch');
  }

  isAuthError(error: Error): boolean {
    return error.message.includes('401') || error.message.includes('unauthorized');
  }

  isValidationError(error: Error): boolean {
    return error.message.includes('400') || error.message.includes('validation');
  }

  // Sync segment to MailerLite
  async syncToMailerLite(segmentId: number): Promise<{
    success: boolean;
    segment_name: string;
    mailerlite_group_id: number;
    mailerlite_group_name: string;
    results: {
      leads_found: number;
      synced: number;
      errors: number;
      details: Array<{ email: string; status: 'success' | 'error'; error?: string }>;
    };
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/segments/${segmentId}/sync-to-mailerlite`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }
}

const segmentService = new SegmentService();
export default segmentService;